/* eslint-disable camelcase */
import * as React from 'react';
import * as Styled from './Typeform.styles';
import {
    sendBrazeCustomEvent,
    updateCart,
    useAppshellMeta,
    useAppShellPush,
    useAppshellRouter,
    useFeatureToggle,
    useLogger,
    useSegmentTracking,
} from '@ids/appshell';
import { useFetchAPI, useIdsGaTracking } from '@ids/hooks';
import useOverstepPage from '@ids/pages/overstep/useOverstepPage';
import { withDelayedRetry } from '@ids/helpers/async';
import { AsyncReturnType } from '@ids/types/utility';
import { useQuestionnairePageContext } from '@ids/context/QuestionnairePage';

const Typeform = () => {
    const { shopBellaCart } = useFeatureToggle();
    const { isApp } = useAppshellMeta();
    const { routes } = useAppshellRouter();
    const { trackIdsEvent } = useIdsGaTracking();
    const { track: trackSegment } = useSegmentTracking();
    const appshellPush = useAppShellPush();
    const { makeRequest } = useFetchAPI();
    const log = useLogger();
    const { enableRedirectsToOverstep } = useOverstepPage();
    const questionnairePageData = useQuestionnairePageContext();

    if (!questionnairePageData?.typeformId) {
        return null;
    }

    const onReady = () => {
        sendBrazeCustomEvent({ name: 'idsQuestionnaireStarted' });
        trackIdsEvent({
            action: 'Screen show',
            label: 'typeform_view',
        });
        trackSegment('IDS Start Request Event');
    };

    const onComplete = async ({ responseId }: { responseId: string }) => {
        log.info('IDS Typeform submitted');
        sendBrazeCustomEvent({ name: 'idsQuestionnaireCompleted' });
        trackIdsEvent({
            action: 'Questionnaire Interaction',
            label: 'typeform_completed',
        });

        const url = `web/v1/ids/form/${questionnairePageData.typeformId}/${responseId}/rooms`;

        const getFormResponse = async () => {
            const formResponse = await makeRequest<{ rooms_count: number }>(url);
            if (!formResponse.success || formResponse.errors.length > 0) {
                throw formResponse;
            }
            return formResponse;
        };

        const formResponse: AsyncReturnType<typeof getFormResponse> = await withDelayedRetry(getFormResponse, {
            retries: 2,
            retryDelay: 300,
        }).catch(err => err);

        if (!formResponse.success) {
            log.error(`IDS Typeform - onComplete error: Request to "${url}" was not successful`);
        }

        const addToCartResponse = await makeRequest('cart/add', {
            queryParams: {
                quantity: formResponse?.data?.rooms_count ?? 1,
                simplesku: questionnairePageData.idsProductSimpleSku,
            },
        });

        if (!addToCartResponse.success) {
            log.error(`IDS Typeform - onComplete error: Product was not successfully added to cart`);
        }

        updateCart();

        enableRedirectsToOverstep(questionnairePageData.idsProductSimpleSku);

        if (shopBellaCart) {
            appshellPush(isApp ? routes.CART_NATIVE : routes.CART);
        } else {
            appshellPush(routes.ALICE_CART);
        }
    };

    return (
        <Styled.TypeFormWidget
            id={questionnairePageData.typeformId}
            onSubmit={onComplete}
            onReady={onReady}
            inlineOnMobile
        />
    );
};

export default Typeform;
